import type { AxiosInstance } from 'axios';
import { BOOKING_KIND_LABELS, ORGANIZER_KIND_LABELS, REGISTRATION_MODE_LABELS, formatEuro } from '../types';
import { CATEGORY_LABELS, EVENT_TYPE_LABELS, PROMOTION_TIER_LABELS } from '../brand';
import { formatDayShort } from '../dateFormat';
import { AUDIT_AKTION_LABEL } from './system';
import { feldLabel, werbeplatzLabel } from './auswertung';

/**
 * Verlauf der Event-Übersicht (`GET /admin/activity`), für Web und App.
 *
 * Das Backend bündelt Audit-Log-Zeilen zu Handlungen und löst Namen auf (`ActivityFeedService`);
 * hier entsteht daraus der Satz, den man lesen kann: „roman hat den Vorschlag vom Event-Enricher
 * zu Gravel Gellersen übernommen" statt „Vorschlag #2324 angenommen durch roman". Die Plattform
 * zeichnet nur noch: Symbol und Farbe je `ton`, Links je `ziel`.
 */

// ── Antwort des Servers ──────────────────────────────────────────────────────────────────────

export type AktivitaetAkteurArt = 'PERSON' | 'AGENT' | 'SOURCE' | 'GUEST' | 'SYSTEM';

export interface AktivitaetAkteur {
  kind: AktivitaetAkteurArt;
  userId?: number | null;
  name?: string | null;
  role?: string | null;
  /** Veranstalter, für den das Konto eingereicht hat. */
  organizer?: string | null;
}

export interface AktivitaetBezug {
  id: number;
  title: string | null;
  /** `false`: inzwischen gelöscht, der Titel stammt aus dem Log. */
  exists: boolean;
}

export interface AktivitaetVorschlag {
  id: number;
  type: 'CREATE' | 'EDIT' | string | null;
  title: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'MERGED' | string | null;
  proposer: AktivitaetAkteur | null;
}

export interface AktivitaetAenderungRoh {
  field: string;
  from: unknown;
  to: unknown;
}

export interface AktivitaetEintrag {
  id: number;
  at: string;
  action: string;
  actions: string[];
  actor: AktivitaetAkteur;
  event?: AktivitaetBezug | null;
  organizer?: AktivitaetBezug | null;
  news?: AktivitaetBezug | null;
  proposal?: AktivitaetVorschlag | null;
  changes: AktivitaetAenderungRoh[];
  info: Record<string, unknown>;
  rowCount: number;
  /** Wie oft dieselbe Bearbeitung kurz nacheinander geschah („dreimal bearbeitet"). */
  times?: number;
}

export interface AktivitaetSeite {
  items: AktivitaetEintrag[];
  /** Zeiger für die nächste Seite; `null` = alles geladen. */
  nextBefore: number | null;
}

export interface AktivitaetAkteurOption {
  userId: number;
  name: string;
  role: 'ADMIN' | 'MODERATOR' | string;
}

export function createAktivitaetService(api: AxiosInstance) {
  return {
    /** `akteur`: `user:<id>`, `HUMAN`, `AGENT`, `SYSTEM` oder leer; `bereich`: Schlüssel aus `AKTIVITAET_BEREICHE`. */
    seite: async (before: number | null, akteur: string, bereich = '', size = 30): Promise<AktivitaetSeite> => {
      const params: Record<string, unknown> = { size };
      if (before != null) params.before = before;
      if (akteur) params.actor = akteur;
      if (bereich) params.area = bereich;
      return (await api.get<AktivitaetSeite>('/admin/activity', { params })).data;
    },
    akteure: async (): Promise<AktivitaetAkteurOption[]> =>
      (await api.get<AktivitaetAkteurOption[]>('/admin/activity/actors')).data,
  };
}

/** Auswahl „Wer": Redaktion mit Namen, dazu die Sammelgruppen. */
export function aktivitaetAkteurOptionen(personen: AktivitaetAkteurOption[]): { key: string; label: string }[] {
  return [
    { key: '', label: 'Alle' },
    ...personen.map(p => ({ key: `user:${p.userId}`, label: `${p.name} (${p.role === 'ADMIN' ? 'Admin' : 'Redaktion'})` })),
    { key: 'HUMAN', label: 'Alle Menschen' },
    { key: 'AGENT', label: 'Agenten' },
    { key: 'SYSTEM', label: 'Import und System' },
  ];
}

/**
 * Auswahl „Was", gespiegelt in `ActivityFeedService.AREAS`. Eingeordnet wird nach der Handlung,
 * nicht nach der Tabelle: eine Übernahme ist ein Vorschlag, auch wenn sie das Event ändert.
 */
export const AKTIVITAET_BEREICHE: { key: string; label: string }[] = [
  { key: '', label: 'Alles' },
  { key: 'EVENTS', label: 'Events' },
  { key: 'PROPOSALS', label: 'Vorschläge' },
  { key: 'ORGANIZERS', label: 'Veranstalter' },
  { key: 'BOOKINGS', label: 'Bestellungen' },
  { key: 'NEWS', label: 'News' },
];

// ── Namen ────────────────────────────────────────────────────────────────────────────────────

const AGENTEN: Record<string, string> = {
  'openclaw-event-enricher': 'Event-Enricher',
  'openclaw-organizer-enricher': 'Veranstalter-Enricher',
  'openclaw-prorace-enricher': 'Profi-Rennen-Enricher',
  'openclaw-outreach-writer': 'Outreach-Schreiber',
};

const QUELLEN: Record<string, string> = {
  'radsport-events': 'rad-events.de',
  'radsport-events-de': 'rad-events.de',
  gctouren: 'GC-Touren',
  strava: 'Strava',
  breitensport: 'Rad-Net Breitensport',
};

/** Wie ein Akteur im Satz heißt. */
export function akteurName(a: AktivitaetAkteur | null | undefined): string {
  if (!a) return 'Unbekannt';
  switch (a.kind) {
    case 'PERSON': return a.name || `Konto ${a.userId ?? ''}`.trim();
    case 'AGENT': return (a.name && AGENTEN[a.name]) || a.name || 'Ein Agent';
    case 'SOURCE': return (a.name && QUELLEN[a.name]) || a.name || 'Ein Import';
    case 'GUEST': return a.name ? `${a.name} (ohne Konto)` : 'Ein Gast ohne Konto';
    default: return 'Das System';
  }
}

// ── Satz ─────────────────────────────────────────────────────────────────────────────────────

/** Wohin ein Satzteil führt. Die Plattform macht daraus ihren Pfad. */
export type AktivitaetZiel =
  | { typ: 'event'; id: number }
  | { typ: 'veranstalter'; id: number }
  | { typ: 'vorschlaege' };

/** Ein Stück des Satzes. `stark`: Name, fett gesetzt; mit `ziel` als Link. */
export interface SatzTeil {
  text: string;
  stark?: boolean;
  ziel?: AktivitaetZiel;
}

/**
 * Art der Handlung, für Symbol und Farbe:
 * neu · bearbeitet · ergaenzt · eingereicht · angenommen · abgelehnt · geloescht · geld ·
 * duplikat · warnung · sonst.
 */
export type AktivitaetTon =
  | 'neu' | 'bearbeitet' | 'ergaenzt' | 'eingereicht' | 'angenommen' | 'abgelehnt'
  | 'geloescht' | 'geld' | 'duplikat' | 'warnung' | 'sonst';

export interface AktivitaetAenderung {
  feld: string;
  /** Alter und neuer Wert, wo beide kurz genug zum Lesen sind. */
  vorher?: string | null;
  nachher?: string | null;
  /** Statt eines Wertepaars („neu gefasst", „2 hinzugefügt: …"). */
  text?: string;
}

export interface AktivitaetZeile {
  ton: AktivitaetTon;
  satz: SatzTeil[];
  /** Zweite Zeile: Stand des Vorschlags, Anhänge, Betrag, Zusätze. */
  hinweise: string[];
  /** Begründung der Redaktion beim Ablehnen oder Übernehmen. */
  kommentar: string | null;
  aenderungen: AktivitaetAenderung[];
  /**
   * Bei mehr als `AENDERUNGEN_OFFEN` Änderungen die eingeklappte Fassung: „Stadt, Links, Straße
   * und 4 weitere". `null` = die Liste ist kurz genug, sie steht gleich offen da.
   */
  aenderungenKurz: string | null;
}

/** Bis zu so vielen Änderungen steht die Liste offen, darüber eingeklappt mit `aenderungenKurz`. */
export const AENDERUNGEN_OFFEN = 2;

function kurzfassung(a: AktivitaetAenderung[]): string | null {
  if (a.length <= AENDERUNGEN_OFFEN) return null;
  const namen = a.slice(0, 3).map(x => x.feld);
  const rest = a.length - namen.length;
  return rest > 0 ? `${namen.join(', ')} und ${rest} weitere` : `${namen.slice(0, -1).join(', ')} und ${namen[namen.length - 1]}`;
}

const TON: Record<string, AktivitaetTon> = {
  CREATE_EVENT: 'neu', SYNC_CREATED: 'neu', CREATE_ORGANIZER: 'neu', CREATE_ORGANIZER_FROM_TEAM: 'neu',
  CREATE_NEWS_POST: 'neu',
  UPDATE_EVENT: 'bearbeitet', SYNC_UPDATED: 'bearbeitet', UPDATE_STATUS: 'bearbeitet', NEW_EDITION: 'bearbeitet',
  ROLL_FORWARD_EVENT: 'bearbeitet', SET_EVENT_PROMOTION: 'bearbeitet', CLEAR_EVENT_PROMOTION: 'bearbeitet',
  UPLOAD_EVENT_IMAGE: 'bearbeitet', UPLOAD_EVENT_FLYER: 'bearbeitet', UPLOAD_EVENT_GPX: 'bearbeitet',
  SET_ORGANIZER_KIND: 'bearbeitet', SET_ORGANIZER_VERIFICATION: 'bearbeitet', LINK_ORGANIZER: 'bearbeitet',
  ENABLE_ORGANIZER_AUTO_APPROVE: 'bearbeitet', DISABLE_ORGANIZER_AUTO_APPROVE: 'bearbeitet',
  UPDATE_ORGANIZER_FROM_OUTREACH: 'bearbeitet', UPDATE_NEWS_POST: 'bearbeitet', UPDATE_NEWS_POST_STATUS: 'bearbeitet',
  UPLOAD_NEWS_COVER: 'bearbeitet', UPDATE_AD_BANNER: 'bearbeitet', UPDATE_EMAIL_TYPE_SETTING: 'bearbeitet',
  SYNC_ENRICHED: 'ergaenzt', AGENT_ENRICH: 'ergaenzt', ENQUEUE_ENRICHMENT: 'ergaenzt',
  EVENT_REFRESHED_FROM_BREITENSPORT: 'ergaenzt', EVENT_DISTANCES_REFRESHED: 'ergaenzt', MIGRATE_LEGACY_IMAGE: 'ergaenzt',
  PROPOSE_CREATE: 'eingereicht', PROPOSE_EDIT: 'eingereicht', PROPOSE_PUBLISH: 'eingereicht', PROPOSE_IMAGE: 'eingereicht',
  PROPOSE_FLYER: 'eingereicht', PROPOSE_GPX: 'eingereicht', PROPOSE_EVENT_IMAGE: 'eingereicht', SUPERSEDE_EDITS: 'eingereicht',
  REQUEST_ORGANIZER_ACCESS: 'eingereicht', CREATE_BOOKING_REQUEST: 'eingereicht', REQUEST_OUTREACH_DRAFT: 'eingereicht',
  APPROVE_EDIT: 'angenommen', APPROVE_ALL_PENDING_EDITS: 'angenommen', APPROVE_EVENT_IMAGE: 'angenommen',
  APPROVE_ORGANIZER_ASSET: 'angenommen', APPROVE_ORGANIZER_ACCESS: 'angenommen', GRANT_ORGANIZER_ACCESS: 'angenommen',
  AUTO_APPROVE_ORGANIZER_ACCESS: 'angenommen', APPROVE_ORGANIZER_ACCESS_BY_EMAIL: 'angenommen',
  CLAIM_ORGANIZER_ACCESS_BY_TOKEN: 'angenommen', APPLY_BOOKING_REQUEST: 'angenommen', MERGE_EDITS: 'angenommen',
  REJECT_EDIT: 'abgelehnt', REJECT_ALL_PENDING_EDITS: 'abgelehnt', REJECT_EVENT_IMAGE: 'abgelehnt',
  REJECT_ORGANIZER_ASSET: 'abgelehnt', REJECT_ORGANIZER_ACCESS: 'abgelehnt', REVOKE_ORGANIZER_ACCESS: 'abgelehnt',
  CLEAR_ORGANIZER_VERIFICATION: 'abgelehnt',
  DELETE_EVENT: 'geloescht', DELETE_EVENT_IMAGE: 'geloescht', DELETE_EVENT_FLYER: 'geloescht',
  DELETE_NEWS_POST: 'geloescht', DELETE_NEWS_COVER: 'geloescht',
  BOOKING_PAID: 'geld',
  MERGE_EVENTS: 'duplikat', DISMISS_DUPLICATE_GROUP: 'duplikat', DISMISS_ORGANIZER_DUPLICATE_GROUP: 'duplikat',
  EVENT_DUPLICATE_BLOCKED: 'warnung', EVENT_SOURCE_DEAD: 'warnung', EVENT_SOURCE_UNPARSABLE: 'warnung',
  EVENT_DISTANCE_DISCREPANCY: 'warnung',
};

const MAL = ['', 'einmal', 'zweimal', 'dreimal', 'viermal', 'fünfmal', 'sechsmal', 'siebenmal', 'achtmal', 'neunmal', 'zehnmal', 'elfmal', 'zwölfmal'];
const malWort = (n: number): string => MAL[n] ?? `${n}\u00a0Mal`;

const zahlAus = (v: unknown): number | null => (typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v)) ? Number(v) : null);
const textAus = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);

const EVENT_STATUS: Record<string, string> = { PUBLISHED: 'Veröffentlicht', DRAFT: 'Entwurf', ARCHIVED: 'Archiviert' };
const ASSET: Record<string, string> = { BANNER: 'das Banner', LOGO: 'das Logo' };

/** Der Satz zu einem Eintrag, ohne Plattformbezug. */
export function aktivitaetZeile(e: AktivitaetEintrag): AktivitaetZeile {
  const info = e.info || {};
  const hinweise: string[] = [];
  const teile: SatzTeil[] = [{ text: akteurName(e.actor), stark: true }];
  const t = (text: string) => teile.push({ text });
  // „dreimal bearbeitet", wenn der Server wiederholte Bearbeitungen zusammengefasst hat. Als Wort,
  // weil „3-mal" auf schmalen Bildschirmen am Bindestrich umbricht.
  const mal = (verb: string) => t((e.times ?? 1) > 1 ? ` ${malWort(e.times ?? 1)} ${verb}` : ` ${verb}`);

  const eventTeil = (): SatzTeil => {
    const ev = e.event;
    if (!ev) return { text: e.proposal?.title || 'ein Event', stark: true };
    const name = ev.title || `Event ${ev.id}`;
    return ev.exists ? { text: name, stark: true, ziel: { typ: 'event', id: ev.id } } : { text: name, stark: true };
  };
  const orgTeil = (): SatzTeil => {
    const o = e.organizer;
    if (!o) return { text: textAus(info.organizer) || 'einen Veranstalter', stark: true };
    const name = o.title || `Veranstalter ${o.id}`;
    return o.exists ? { text: name, stark: true, ziel: { typ: 'veranstalter', id: o.id } } : { text: name, stark: true };
  };
  const newsTeil = (): SatzTeil => ({ text: e.news?.title || textAus(info.title) || 'einen Artikel', stark: true });
  const ev = () => teile.push(eventTeil());
  const org = () => teile.push(orgTeil());
  const p = e.proposal;
  const vonEinreicher = () => {
    // Wer den Vorschlag geschrieben hat, wenn es nicht der Handelnde selbst ist.
    const pr = p?.proposer;
    if (!pr) return;
    if (pr.kind === 'GUEST' && !pr.name) { t(' von einem Gast ohne Konto'); return; }
    t(pr.kind === 'AGENT' ? ' vom ' : ' von ');
    teile.push({ text: akteurName(pr) });
    const zusatz = pr.organizer ? ` für ${pr.organizer}` : '';
    if (zusatz) t(zusatz);
  };
  const selbst = !!p?.proposer && p.proposer.kind === e.actor.kind
    && (p.proposer.userId ?? null) === (e.actor.userId ?? null) && (p.proposer.name ?? null) === (e.actor.name ?? null);
  const neuesEvent = p?.type === 'CREATE';

  switch (e.action) {
    case 'APPROVE_EDIT':
      if (selbst) {
        t(' hat '); ev(); t(neuesEvent ? ' eingetragen' : ' bearbeitet');
        hinweise.push('ohne Prüfung übernommen');
      } else if (neuesEvent) {
        t(' hat '); ev(); t(' als neues Event aufgenommen, Vorschlag'); vonEinreicher();
      } else {
        t(' hat den Vorschlag'); vonEinreicher(); t(' zu '); ev(); t(' übernommen');
      }
      break;
    case 'REJECT_EDIT':
      if (neuesEvent) {
        t(' hat den Vorschlag'); vonEinreicher(); t(' für das neue Event '); teile.push({ text: p?.title || 'ohne Titel', stark: true }); t(' abgelehnt');
      } else {
        t(' hat den Vorschlag'); vonEinreicher(); t(' zu '); ev(); t(' abgelehnt');
      }
      break;
    case 'PROPOSE_CREATE':
      t(' hat das neue Event '); teile.push({ text: p?.title || textAus(info.title) || 'ohne Titel', stark: true }); t(' vorgeschlagen');
      break;
    case 'PROPOSE_EDIT':
      t(' hat eine Änderung an '); ev(); t(' vorgeschlagen');
      break;
    case 'PROPOSE_PUBLISH':
      t(' hat vorgeschlagen, '); ev(); t(' zu veröffentlichen');
      break;
    case 'PROPOSE_IMAGE': case 'PROPOSE_FLYER': case 'PROPOSE_GPX': {
      const was = e.action === 'PROPOSE_IMAGE' ? 'ein Bild' : e.action === 'PROPOSE_FLYER' ? 'einen Flyer' : 'einen GPX-Track';
      t(` hat ${was} zum Vorschlag für `); ev(); t(' nachgereicht');
      break;
    }
    case 'SUPERSEDE_EDITS':
      t(' hat den eigenen offenen Vorschlag zu '); ev(); t(' ersetzt');
      break;
    case 'MERGE_EDITS': {
      const n = Array.isArray(info.merged) ? info.merged.length : 1;
      t(n === 1 ? ' hat einen weiteren Vorschlag zu ' : ` hat ${n} weitere Vorschläge zu `); ev(); t(' mit erledigt');
      break;
    }
    case 'APPROVE_ALL_PENDING_EDITS': case 'REJECT_ALL_PENDING_EDITS': {
      const n = zahlAus(info.count);
      t(` hat alle offenen Vorschläge${n != null ? ` (${n})` : ''} ${e.action.startsWith('APPROVE') ? 'übernommen' : 'abgelehnt'}`);
      break;
    }

    case 'CREATE_EVENT': t(' hat '); ev(); t(' angelegt'); break;
    case 'SYNC_CREATED': t(' hat '); ev(); t(' importiert'); break;
    case 'UPDATE_EVENT': t(' hat '); ev(); mal('bearbeitet'); break;
    case 'SYNC_UPDATED': t(' hat '); ev(); mal('aktualisiert'); break;
    case 'SYNC_ENRICHED': case 'AGENT_ENRICH': t(' hat '); ev(); mal('ergänzt'); break;
    case 'UPDATE_STATUS': {
      const neu = textAus(info.newStatus);
      t(' hat '); ev();
      t(neu === 'PUBLISHED' ? ' veröffentlicht' : neu === 'ARCHIVED' ? ' archiviert' : neu === 'DRAFT' ? ' zurück auf Entwurf gesetzt' : ' umgestellt');
      break;
    }
    case 'NEW_EDITION': case 'ROLL_FORWARD_EVENT': t(' hat '); ev(); t(' ins nächste Jahr fortgeschrieben'); break;
    case 'DELETE_EVENT': t(' hat '); ev(); t(' gelöscht'); break;
    case 'MERGE_EVENTS': {
      const n = textAus(info.mergedIds)?.split(',').length ?? 1;
      t(n === 1 ? ' hat ein Duplikat in ' : ` hat ${n} Duplikate in `); ev(); t(' zusammengeführt');
      break;
    }
    case 'DISMISS_DUPLICATE_GROUP': t(' hat den Duplikat-Verdacht bei '); ev(); t(' verworfen'); break;
    case 'EVENT_DUPLICATE_BLOCKED': t(' hat ein mögliches Duplikat von '); ev(); t(' übersprungen'); break;
    case 'SET_EVENT_PROMOTION': {
      t(' hat '); ev(); t(' hervorgehoben');
      const stufe = textAus(info.tier);
      if (stufe) hinweise.push(PROMOTION_TIER_LABELS[stufe] ?? stufe);
      const bis = textAus(info.until);
      if (bis) hinweise.push(`bis ${formatDayShort(bis.slice(0, 10))}`);
      break;
    }
    case 'CLEAR_EVENT_PROMOTION': t(' hat die Hervorhebung von '); ev(); t(' beendet'); break;
    case 'UPLOAD_EVENT_IMAGE': t(' hat ein Bild für '); ev(); t(' hochgeladen'); break;
    case 'DELETE_EVENT_IMAGE': t(' hat das Bild von '); ev(); t(' entfernt'); break;
    case 'UPLOAD_EVENT_FLYER': t(' hat einen Flyer für '); ev(); t(' hochgeladen'); break;
    case 'DELETE_EVENT_FLYER': t(' hat den Flyer von '); ev(); t(' entfernt'); break;
    case 'UPLOAD_EVENT_GPX': t(' hat einen GPX-Track für '); ev(); t(' hochgeladen'); break;
    case 'PROPOSE_EVENT_IMAGE': t(' hat ein Bild für '); ev(); t(' vorgeschlagen'); if (info.aiGenerated) hinweise.push('KI-Bild'); break;
    case 'APPROVE_EVENT_IMAGE': t(' hat das vorgeschlagene Bild für '); ev(); t(' übernommen'); break;
    case 'REJECT_EVENT_IMAGE': t(' hat das vorgeschlagene Bild für '); ev(); t(' abgelehnt'); break;
    case 'ENQUEUE_ENRICHMENT': t(' hat '); ev(); t(' zum erneuten Ergänzen vorgemerkt'); break;
    case 'EVENT_REFRESHED_FROM_BREITENSPORT': t(' hat '); ev(); t(' mit dem Breitensportkalender abgeglichen'); break;
    case 'EVENT_DISTANCES_REFRESHED': t(' hat Strecken bei '); ev(); t(' ergänzt'); break;
    case 'EVENT_DISTANCE_DISCREPANCY': t(' meldet abweichende Strecken bei '); ev(); break;
    case 'EVENT_SOURCE_DEAD': t(' erreicht die Quelle von '); ev(); t(' nicht mehr'); break;
    case 'EVENT_SOURCE_UNPARSABLE': t(' kann die Quelle von '); ev(); t(' nicht lesen'); break;

    case 'CREATE_ORGANIZER': t(' hat '); org(); t(' als Veranstalter angelegt'); break;
    case 'CREATE_ORGANIZER_FROM_TEAM': t(' hat '); org(); t(' über die Winterchallenge als Veranstalter angelegt'); break;
    case 'LINK_ORGANIZER': t(' hat '); org(); t(' mit einem Event verknüpft'); break;
    case 'SET_ORGANIZER_KIND': t(' hat '); org(); t(` als ${(ORGANIZER_KIND_LABELS as Record<string, string>)[textAus(info.to) ?? ''] ?? 'Veranstalter'} eingeordnet`); break;
    case 'SET_ORGANIZER_VERIFICATION': {
      t(' hat Premium für '); org(); t(' freigeschaltet');
      const m = zahlAus(info.months);
      if (m) hinweise.push(m === 1 ? '1 Monat' : `${m} Monate`);
      break;
    }
    case 'CLEAR_ORGANIZER_VERIFICATION': t(' hat Premium für '); org(); t(' beendet'); break;
    case 'ENABLE_ORGANIZER_AUTO_APPROVE': t(' lässt Änderungen von '); org(); t(' ab jetzt ohne Prüfung durch'); break;
    case 'DISABLE_ORGANIZER_AUTO_APPROVE': t(' prüft Änderungen von '); org(); t(' wieder vor der Übernahme'); break;
    case 'APPROVE_ORGANIZER_ASSET': case 'REJECT_ORGANIZER_ASSET':
      t(` hat ${ASSET[textAus(info.kind) ?? ''] ?? 'das Bild'} von `); org();
      t(e.action.startsWith('APPROVE') ? ' freigegeben' : ' abgelehnt');
      break;
    case 'DISMISS_ORGANIZER_DUPLICATE_GROUP': t(' hat den Duplikat-Verdacht bei '); org(); t(' verworfen'); break;
    case 'UPDATE_ORGANIZER_FROM_OUTREACH': {
      t(' hat Kontaktdaten aus dem Outreach bei '); org(); t(' übernommen');
      const f = textAus(info.fields);
      if (f) hinweise.push(f);
      break;
    }
    case 'REQUEST_ORGANIZER_ACCESS': t(' hat Zugang zu '); org(); t(' beantragt'); break;
    case 'GRANT_ORGANIZER_ACCESS': t(' hat einem Konto Zugang zu '); org(); t(' gegeben'); break;
    case 'APPROVE_ORGANIZER_ACCESS': t(' hat einen Antrag auf Zugang zu '); org(); t(' angenommen'); break;
    case 'REJECT_ORGANIZER_ACCESS': t(' hat einen Antrag auf Zugang zu '); org(); t(' abgelehnt'); break;
    case 'REVOKE_ORGANIZER_ACCESS': t(' hat einem Konto den Zugang zu '); org(); t(' entzogen'); break;
    case 'AUTO_APPROVE_ORGANIZER_ACCESS': t(' hat Zugang zu '); org(); t(' bekommen'); hinweise.push('Adresse passt zur Domain des Veranstalters'); break;
    case 'APPROVE_ORGANIZER_ACCESS_BY_EMAIL': t(' hat Zugang zu '); org(); t(' bekommen'); hinweise.push('vom Veranstalter per Mail bestätigt'); break;
    case 'CLAIM_ORGANIZER_ACCESS_BY_TOKEN': t(' hat Zugang zu '); org(); t(' bekommen'); hinweise.push('über einen Einladungslink'); break;

    case 'CREATE_BOOKING_REQUEST': {
      const k = textAus(info.kind);
      t(` hat ${k ? ((BOOKING_KIND_LABELS as Record<string, string>)[k] ?? k) : 'eine Leistung'} für `); org(); t(' bestellt');
      break;
    }
    case 'BOOKING_PAID': {
      t(' hat die Zahlung von '); org(); t(' verbucht');
      const c = zahlAus(info.amountCents);
      if (c != null) hinweise.push(formatEuro(c));
      if (e.actions.indexOf('SET_ORGANIZER_VERIFICATION') >= 0) {
        const m = zahlAus(info.months);
        hinweise.push(`Premium freigeschaltet${m ? `, ${m === 1 ? '1 Monat' : `${m} Monate`}` : ''}`);
      } else if (e.actions.indexOf('APPLY_BOOKING_REQUEST') >= 0) {
        hinweise.push('Leistung freigeschaltet');
      }
      break;
    }
    case 'APPLY_BOOKING_REQUEST': {
      const k = textAus(info.kind);
      t(` hat ${k ? ((BOOKING_KIND_LABELS as Record<string, string>)[k] ?? k) : 'die Bestellung'} für `); org(); t(' freigeschaltet');
      break;
    }

    case 'CREATE_NEWS_POST': t(' hat den Artikel '); teile.push(newsTeil()); t(' angelegt'); break;
    case 'UPDATE_NEWS_POST': t(' hat den Artikel '); teile.push(newsTeil()); mal('bearbeitet'); break;
    case 'UPDATE_NEWS_POST_STATUS':
      t(' hat den Artikel '); teile.push(newsTeil());
      t(textAus(info.status) === 'PUBLISHED' ? ' veröffentlicht' : textAus(info.status) === 'DRAFT' ? ' zurück auf Entwurf gesetzt' : ' umgestellt');
      break;
    case 'DELETE_NEWS_POST': t(' hat den Artikel '); teile.push(newsTeil()); t(' gelöscht'); break;
    case 'UPLOAD_NEWS_COVER': t(' hat ein Titelbild für '); teile.push(newsTeil()); t(' hochgeladen'); break;
    case 'DELETE_NEWS_COVER': t(' hat das Titelbild von '); teile.push(newsTeil()); t(' entfernt'); break;

    case 'REQUEST_OUTREACH_DRAFT': t(' hat einen Outreach-Entwurf beim Schreib-Agenten bestellt'); break;
    case 'UPDATE_AD_BANNER': {
      const key = textAus(info.key);
      t(` hat die Anzeige `); teile.push({ text: key ? werbeplatzLabel(key) : 'Werbeplatz', stark: true });
      t(info.active === false ? ' abgeschaltet' : info.active === true ? ' eingeschaltet' : ' geändert');
      break;
    }
    case 'UPDATE_EMAIL_TYPE_SETTING': t(' hat eine Mail-Einstellung geändert'); break;
    case 'RESET_AGENT_STATE': t(' hat einen Agent-Status zurückgesetzt'); break;
    case 'MIGRATE_LEGACY_IMAGE': t(' hat ein Bild von der alten Website übernommen'); break;

    default: {
      t(`: ${AUDIT_AKTION_LABEL[e.action] ?? e.action}`);
      if (e.event) { t(', '); ev(); } else if (e.organizer) { t(', '); org(); } else if (e.news) { t(', '); teile.push(newsTeil()); }
    }
  }

  // Stand eines eingereichten Vorschlags: was ist inzwischen daraus geworden?
  if (p && e.action.startsWith('PROPOSE')) {
    const anhaenge = [
      e.actions.indexOf('PROPOSE_IMAGE') >= 0 && e.action !== 'PROPOSE_IMAGE' ? 'Bild' : null,
      e.actions.indexOf('PROPOSE_FLYER') >= 0 && e.action !== 'PROPOSE_FLYER' ? 'Flyer' : null,
      e.actions.indexOf('PROPOSE_GPX') >= 0 && e.action !== 'PROPOSE_GPX' ? 'GPX-Track' : null,
    ].filter(Boolean);
    if (anhaenge.length) hinweise.push(`mit ${anhaenge.join(', ')}`);
    if (e.actions.indexOf('SUPERSEDE_EDITS') >= 0 && e.action !== 'SUPERSEDE_EDITS') hinweise.push('ersetzt den vorigen eigenen Vorschlag');
    hinweise.push(
      p.status === 'PENDING' ? 'wartet auf Prüfung'
        : p.status === 'APPROVED' ? 'inzwischen übernommen'
        : p.status === 'REJECTED' ? 'inzwischen abgelehnt'
        : p.status === 'MERGED' ? 'in einem späteren Vorschlag aufgegangen'
        : '',
    );
  }
  if (e.event && !e.event.exists && e.action !== 'DELETE_EVENT') hinweise.push('Event inzwischen gelöscht');
  if (e.organizer && !e.organizer.exists) hinweise.push('Veranstalter inzwischen gelöscht');

  const kommentar = textAus(info.comment) && !/^Bulk-Genehmigung/.test(String(info.comment)) ? String(info.comment) : null;
  const aenderungen = aktivitaetAenderungen(e.changes || []);
  return {
    ton: TON[e.action] ?? 'sonst',
    satz: teile,
    hinweise: hinweise.filter(Boolean),
    kommentar,
    aenderungen,
    aenderungenKurz: kurzfassung(aenderungen),
  };
}

// ── Änderungen ───────────────────────────────────────────────────────────────────────────────

/** Technische Felder, gespiegelt in `ActivityFeedService.NOISE_FIELDS`. */
export const AKTIVITAET_RAUSCH_FELDER = new Set([
  'externalId', 'sourceUrl', 'source', 'lastSeenAt', 'firstSeenAt', 'updatedAt', 'createdAt', 'active',
  'contributedFields', 'contributed_fields',
]);

const ZUSATZ_LABELS: Record<string, string> = {
  orgKind: 'Einordnung', endDate: 'Enddatum', routes: 'Strecken-Dateien', registrationStartTime: 'Anmeldestart (Uhrzeit)',
  registrationStartPrecision: 'Anmeldestart (Genauigkeit)', permanent: 'Jederzeit fahrbar', seasonFrom: 'Saison ab',
  seasonUntil: 'Saison bis', areaName: 'Gebiet', secondaryCategories: 'Weitere Kategorien', imageCredit: 'Bildnachweis',
  priceTiers: 'Preisstufen', organizerName: 'Veranstalter', contactPerson: 'Ansprechperson',
};

const feldName = (f: string) => ZUSATZ_LABELS[f] ?? feldLabel(f);

const LANG = 70;

const kurzeUrl = (u: string) => u.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');

function wert(feld: string, v: unknown): string | null {
  if (v == null || v === '') return null;
  if (Array.isArray(v)) return v.length ? v.map(x => wert(feld, x)).filter(Boolean).join(', ') : null;
  if (typeof v === 'boolean') return v ? 'Ja' : 'Nein';
  if (typeof v === 'number') return v.toLocaleString('de-DE');
  if (typeof v === 'object') return null;
  const s = String(v);
  if (feld === 'status') return EVENT_STATUS[s] ?? s;
  if (feld === 'eventType') return EVENT_TYPE_LABELS[s] ?? s;
  if (feld === 'category') return CATEGORY_LABELS[s] ?? s;
  if (feld === 'registrationMode') return (REGISTRATION_MODE_LABELS as Record<string, string>)[s] ?? s;
  if (feld === 'registrationState') return s === 'OPEN' ? 'Offen' : s === 'CLOSED' ? 'Geschlossen' : 'Nicht bekannt';
  if (feld === 'orgKind') return (ORGANIZER_KIND_LABELS as Record<string, string>)[s] ?? s;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return formatDayShort(s);
  if (/^https?:\/\//.test(s)) return kurzeUrl(s);
  return s;
}

function listenText(vorher: unknown[], nachher: unknown[]): string {
  const a = vorher.map(String);
  const b = nachher.map(String);
  const dazu = b.filter(x => a.indexOf(x) < 0).map(x => (/^https?:/.test(x) ? kurzeUrl(x) : x));
  const weg = a.filter(x => b.indexOf(x) < 0).map(x => (/^https?:/.test(x) ? kurzeUrl(x) : x));
  const s: string[] = [];
  if (dazu.length) s.push(`neu: ${dazu.join(', ')}`);
  if (weg.length) s.push(`entfernt: ${weg.join(', ')}`);
  return s.length ? s.join('; ') : 'überarbeitet';
}

/** Lesbare Änderungen. Breite und Länge werden zu „Koordinaten", lange Texte nur benannt. */
export function aktivitaetAenderungen(roh: AktivitaetAenderungRoh[]): AktivitaetAenderung[] {
  const aus: AktivitaetAenderung[] = [];
  const gesehen: Record<string, boolean> = {};
  for (const c of roh) {
    if (AKTIVITAET_RAUSCH_FELDER.has(c.field)) continue;
    const feld = feldName(c.field);
    if (gesehen[feld]) continue;
    gesehen[feld] = true;
    if (c.field === 'latitude' || c.field === 'longitude') { aus.push({ feld, text: 'angepasst' }); continue; }
    if (c.field === 'routes') { aus.push({ feld, text: c.to == null ? 'entfernt' : c.from == null ? 'hinterlegt' : 'ausgetauscht' }); continue; }
    if (c.field === 'links' || c.field === 'additionalLinks' || (Array.isArray(c.from) && Array.isArray(c.to) && c.field !== 'distances')) {
      aus.push({ feld, text: listenText(Array.isArray(c.from) ? c.from : [], Array.isArray(c.to) ? c.to : []) });
      continue;
    }
    const vorher = wert(c.field, c.from);
    const nachher = wert(c.field, c.to);
    if (vorher === nachher) { aus.push({ feld, text: 'überarbeitet' }); continue; }
    const lang = (vorher?.length ?? 0) > LANG || (nachher?.length ?? 0) > LANG;
    if (lang) {
      aus.push({ feld, text: vorher == null ? 'neu geschrieben' : nachher == null ? 'entfernt' : 'neu gefasst' });
      continue;
    }
    aus.push({ feld, vorher, nachher });
  }
  return aus;
}

/** Tagesüberschrift: „Heute", „Gestern", sonst „Montag, 26. Oktober 2026". */
export function aktivitaetTag(iso: string, jetzt: Date = new Date()): string {
  const d = new Date(iso);
  const tag = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  if (tag(d) === tag(jetzt)) return 'Heute';
  const gestern = new Date(jetzt.getTime() - 86_400_000);
  if (tag(d) === tag(gestern)) return 'Gestern';
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

/** Uhrzeit in der Zeile, „14:03". */
export const aktivitaetUhrzeit = (iso: string): string =>
  new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

/**
 * Zeitangabe ohne Tagesüberschrift, für die Kurzliste auf der Übersicht: „gerade eben",
 * „vor 12 Min.", „vor 3 Std.", „gestern, 14:03", sonst „26.10., 14:03".
 */
export function aktivitaetWann(iso: string, jetzt: Date = new Date()): string {
  const d = new Date(iso);
  const min = Math.floor((jetzt.getTime() - d.getTime()) / 60_000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} Min.`;
  const tag = aktivitaetTag(iso, jetzt);
  if (tag === 'Heute') return `vor ${Math.floor(min / 60)} Std.`;
  const uhr = aktivitaetUhrzeit(iso);
  if (tag === 'Gestern') return `gestern, ${uhr}`;
  return `${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}, ${uhr}`;
}
